/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, Fragment } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-hot-toast";
import { AuthStorage } from "@/lib/storage";
import {
  ClockIcon,
  UserGroupIcon,
  LanguageIcon,
  HeartIcon,
  CurrencyRupeeIcon,
  StarIcon,
  VideoCameraIcon,
  BookOpenIcon,
  XMarkIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  FireIcon,
} from "@heroicons/react/24/outline";
import { FitnessClass, Difficulty } from "@/types/fitness-class";
import ImageOverlay from "./ImageOverlay";
import PaymentFirstBooking from "@/components/classes/PaymentFirstBooking";
import {
  useClassWishlist,
  useToggleClassWishlist,
} from "@/hooks/useClassWishlist";
import { useQueryClient } from "@tanstack/react-query";
import {
  getClassDateTime,
  getBaseAmount,
  isClassExpired,
} from "@/utils/class-helpers";
import { useAuthSession } from "@/hooks/useAuthSession";



interface ClassListProps {
  classes: FitnessClass[];
  hideImage?: boolean;
  simplified?: boolean;
  hideLikeButton?: boolean;
  gridCols?: "sm" | "md" | "lg"; // New prop for controlling card width
  mobileLayout?: "vertical" | "horizontal"; // New prop for mobile layout preference
  layoutMode?: "grid" | "slider"; // New prop to switch between grid and horizontal slider on mobile
  onBookingSuccess?: () => void; // Callback to refresh data after booking
  onSpotUpdate?: (classId: string, newSpotCount: number) => void; // Callback to update spot count
  isLoading?: boolean;
  userBookedClasses?: Set<string>; // Set of class IDs that user has booked
}

const ClassList: React.FC<ClassListProps> = ({
  classes,
  hideImage = false,
  simplified = false,
  hideLikeButton = false,
  gridCols = "lg", // Default to large cards
  mobileLayout = "vertical",
  layoutMode = "grid",
  onBookingSuccess,
  onSpotUpdate,
  userBookedClasses,
}) => {
  const { user, isLoading: isAuthLoading } = useAuthSession();
  const [bookingClass, setBookingClass] = useState<FitnessClass | null>(null);
  const [classSpots, setClassSpots] = useState<Record<string, number>>({});
  const [imageOverlay, setImageOverlay] = useState<{
    isOpen: boolean;
    imageSrc: string;
    imageAlt: string;
    title: string;
  }>({
    isOpen: false,
    imageSrc: "",
    imageAlt: "",
    title: "",
  });
  const router = useRouter();
  const queryClient = useQueryClient();

  // Use React Query for wishlist
  const { data: wishlistIds = [] } = useClassWishlist();
  const toggleWishlist = useToggleClassWishlist();

  // Convert wishlist array to favorites object for backward compatibility
  const favorites = wishlistIds.reduce<Record<string, boolean>>((acc, id) => {
    acc[id] = true;
    return acc;
  }, {});

  // Update wishlist queries when user state changes
  useEffect(() => {
    if (user) {
      // Invalidate wishlist query when user is confirmed logged in
      queryClient.invalidateQueries({ queryKey: ["wishlist", "classes"] });
    }
  }, [user, queryClient]);

  // Initialize class spots from props
  useEffect(() => {
    const spotsMap: Record<string, number> = {};
    classes.forEach((cls) => {
      spotsMap[cls.id] = cls.spotsLeft;
    });
    setClassSpots(spotsMap);
  }, [classes]);

  const handleFavoriteClick = async (classId: string) => {
    // Check if auth is still loading
    if (isAuthLoading) {
      toast.error("Please wait while we verify your login status...");
      return;
    }

    if (!user) {
      toast.error("Please login to add classes to your wishlist");
      router.push("/login?callbackUrl=/classes");
      return;
    }

    // Determine action based on current state
    const isFavorited = favorites[classId];
    const action = isFavorited ? "remove" : "add";

    // Use React Query mutation
    toggleWishlist.mutate({ classId, action });
  };

  const handleBookingClick = (fitnessClass: FitnessClass) => {
    // Check if auth is still loading
    if (isAuthLoading) {
      toast.error("Please wait while we verify your login status...");
      return;
    }

    if (!user) {
      toast.error("Please login to continue");
      router.push("/login?callbackUrl=/classes");
      return;
    }

    setBookingClass(fitnessClass);
  };
  // Function to update spot count after booking
  const handleSpotUpdate = (classId: string, newSpotCount: number) => {
    setClassSpots((prev) => ({
      ...prev,
      [classId]: newSpotCount,
    }));

    // Call parent's onSpotUpdate if provided
    if (onSpotUpdate) {
      onSpotUpdate(classId, newSpotCount);
    }
  };

  // Function to handle image click to open overlay
  const handleImageClick = (fitnessClass: FitnessClass) => {
    setImageOverlay({
      isOpen: true,
      imageSrc:
        fitnessClass.media?.image ||
        "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3",
      imageAlt: fitnessClass.title,
      title: fitnessClass.title,
    });
  };

  // Helper function to get price from pricing structure
  const getDisplayPrice = (fitnessClass: FitnessClass): number => {
    if (!fitnessClass.pricing) return 0;

    switch (fitnessClass.pricing.mode) {
      case "payPerSession":
        return fitnessClass.pricing.sessionPrice;
      case "recurringClass":
        return fitnessClass.pricing.sessionPrice; // Show per-session price for recurring classes
      case "classPack":
        return (
          fitnessClass.pricing.totalPrice / fitnessClass.pricing.classesIncluded
        );
      case "subscription":
        return (
          fitnessClass.pricing.totalPrice / fitnessClass.pricing.durationDays
        );
      case "course":
        return (
          fitnessClass.pricing.totalPrice / fitnessClass.pricing.totalSessions
        );
      case "consultation":
        return fitnessClass.pricing.price;
      default:
        return 0;
    }
  };

  // Helper function to get schedule display info
  const getScheduleInfo = (
    fitnessClass: FitnessClass
  ): { date: string; time: string; duration: string } => {
    // Use classDate from booking if available (for booked classes)
    const bookingClassDate = (fitnessClass as any).classDate;
    const duration = `${fitnessClass.schedule?.durationMin || (fitnessClass as any).duration || 60} min`;

    if (bookingClassDate) {
      // For booked classes, show the actual class date
      const classDate = new Date(bookingClassDate);
      return {
        date: classDate.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        time: classDate.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        duration,
      };
    }

    // For regular classes, don't show TBD - use sensible defaults
    if (!fitnessClass.schedule) {
      return {
        date: "Flexible",
        time: "Contact instructor",
        duration,
      };
    }

    switch (fitnessClass.schedule.kind) {
      case "single":
        const singleDate = new Date(fitnessClass.schedule.date);
        return {
          date: singleDate.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          }),
          time: singleDate.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
          duration,
        };
      case "recurring":
        const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const days =
          fitnessClass.schedule.weekdayMask
            ?.map((day) => weekdays[day])
            .join(", ") || "Weekly";
        return {
          date: days,
          time: fitnessClass.schedule.startTime || "Flexible timing",
          duration,
        };
      case "course":
        return {
          date: `${fitnessClass.schedule.sessionDates?.length || "Multi"} sessions`,
          time: "Various times",
          duration,
        };
      default:
        return {
          date: "Flexible",
          time: "Contact instructor",
          duration,
        };
    }
  };

  // Grid layout configuration based on gridCols prop
  const getGridClasses = () => {
    // SLIDER MODE (Mobile Only)
    if (layoutMode === "slider") {
      const justifyClass = "sm:justify-items-center";
      const gapClass = "gap-4 sm:gap-8";
      const paddingClass = "px-4 pb-4 -mx-4 sm:p-4";

      const gridColsClass = gridCols === "sm"
        ? "sm:grid-cols-2 lg:grid-cols-2"
        : gridCols === "md"
          ? "sm:grid-cols-2 lg:grid-cols-3"
          : simplified
            ? "sm:grid-cols-2 lg:grid-cols-4"
            : "sm:grid-cols-2 lg:grid-cols-4";

      // Mobile: Use grid-flow-col with auto-cols for horizontal scroll with equal heights
      // Desktop: Switch back to normal grid with rows
      return `grid grid-flow-col auto-cols-[85vw] overflow-x-auto snap-x snap-mandatory scrollbar-none sm:grid-flow-row sm:auto-cols-auto ${gridColsClass} ${gapClass} ${paddingClass} ${justifyClass}`;
    }

    // STANDARD GRID MODE
    // For mobile horizontal layout, don't center items on mobile to allow full width
    const justifyClass =
      mobileLayout === "horizontal"
        ? "sm:justify-items-center"
        : "justify-items-center";
    // Use smaller gap on mobile for horizontal layout
    const gapClass = mobileLayout === "horizontal" ? "gap-4 sm:gap-8" : "gap-8";
    // Use smaller padding on mobile for horizontal layout
    const paddingClass = mobileLayout === "horizontal" ? "p-2 sm:p-4" : "p-4";

    if (gridCols === "sm") {
      return `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 ${gapClass} ${paddingClass} ${justifyClass}`; // Larger cards (2 per row)
    } else if (gridCols === "md") {
      return `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${gapClass} ${paddingClass} ${justifyClass}`; // Medium cards (3 per row)
    } else {
      return `grid grid-cols-1 sm:grid-cols-2 ${simplified ? "lg:grid-cols-4" : "lg:grid-cols-4"} ${gapClass} ${paddingClass} ${justifyClass}`; // Original layout
    }
  };

  return (
    <>
      <div
        className={getGridClasses()}
        style={layoutMode === 'slider' ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : undefined}
      >
        {layoutMode === 'slider' && (
          <style jsx>{`
           div::-webkit-scrollbar {
             display: none;
           }
         `}</style>
        )}
        {classes.map((fitnessClass: FitnessClass) => {
          const scheduleInfo = getScheduleInfo(fitnessClass);
          const price = getDisplayPrice(fitnessClass);
          const currentSpots =
            classSpots[fitnessClass.id] ?? fitnessClass.spotsLeft;

          return (
            <div
              key={fitnessClass.id}
              className={`h-full ${layoutMode === "slider" ? "snap-center sm:w-full min-h-[240px]" : mobileLayout === "horizontal" ? "w-full" : "w-full max-w-[350px]"}`}
            >
              <div
                className={`group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 w-full flex h-full ${mobileLayout === "horizontal" ? "flex-row sm:flex-col" : "flex-col"}`}
              >
                {/* Class Image with Overlay */}
                <div
                  className={`relative ${mobileLayout === "horizontal" ? "w-1/3 sm:w-full min-h-[140px]" : "w-full"}`}
                >
                  {!hideImage && (
                    <div
                      className={`relative w-full cursor-pointer group/image ${mobileLayout === "horizontal" ? "h-full sm:h-[200px]" : simplified ? "h-[200px]" : "h-[240px]"}`}
                      onClick={() => handleImageClick(fitnessClass)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleImageClick(fitnessClass);
                        }
                      }}
                      aria-label={`View ${fitnessClass.title} image in full screen`}
                    >
                      <Image
                        src={
                          fitnessClass.media?.image ||
                          "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3"
                        }
                        alt={fitnessClass.title}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />

                      {/* Zoom Icon Overlay on Hover */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 bg-black/20">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 transform scale-90 group-hover/image:scale-100 transition-transform duration-300">
                          <svg
                            className="w-6 h-6 text-gray-800"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Type and Difficulty Badges */}
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    <span className="px-2.5 py-1 bg-orange-500 backdrop-blur-sm rounded-full text-xs font-semibold text-white shadow-lg border border-orange-400/50">
                      {fitnessClass.category}
                    </span>
                    {!simplified && (
                      <>
                        <span
                          className={`px-2.5 py-1 bg-gray-800 backdrop-blur-sm rounded-full text-xs font-medium text-white shadow-lg border border-gray-600/50 ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                        >
                          {Array.isArray(fitnessClass.difficulty)
                            ? fitnessClass.difficulty[0]
                            : fitnessClass.difficulty}
                        </span>
                        <span
                          className={`px-2.5 py-1 bg-purple-600 backdrop-blur-sm rounded-full text-xs font-medium text-white shadow-lg border border-purple-500/50 ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                        >
                          {fitnessClass.classType}
                        </span>
                      </>
                    )}

                    {/* Booking Status Badge - Show if this is a booked class */}
                    {(fitnessClass as any).bookingStatus && (
                      <span
                        className={`px-2.5 py-1 backdrop-blur-sm rounded-full text-xs font-semibold shadow-lg border ${(fitnessClass as any).bookingStatus === "confirmed" &&
                          (fitnessClass as any).paymentStatus === "paid"
                          ? "bg-green-600 text-white border-green-500/50"
                          : (fitnessClass as any).bookingStatus ===
                            "confirmed" &&
                            (fitnessClass as any).paymentStatus ===
                            "pending"
                            ? "bg-orange-600 text-white border-orange-500/50"
                            : (fitnessClass as any).bookingStatus ===
                              "pending"
                              ? "bg-yellow-600 text-white border-yellow-500/50"
                              : "bg-red-600 text-white border-red-500/50"
                          }`}
                      >
                        {(fitnessClass as any).bookingStatus === "confirmed" &&
                          (fitnessClass as any).paymentStatus === "paid"
                          ? "Booked"
                          : (fitnessClass as any).bookingStatus ===
                            "confirmed" &&
                            (fitnessClass as any).paymentStatus === "pending"
                            ? "Pay Pending"
                            : (fitnessClass as any).bookingStatus === "pending"
                              ? "Booking Pending"
                              : "Cancelled"}
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div
                    className={`absolute top-3 right-3 flex gap-2 ${mobileLayout === "horizontal" ? "hidden sm:flex" : ""}`}
                  >
                    {!hideLikeButton && (
                      <button
                        onClick={() => handleFavoriteClick(fitnessClass.id)}
                        className={`p-2 rounded-full backdrop-blur-sm transition-all duration-300 ${favorites[fitnessClass.id]
                          ? "bg-[#ff7043] text-white"
                          : "bg-white/20 text-white hover:bg-[#ff7043]/20"
                          }`}
                      >
                        <HeartIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Instructor Information Overlay - Hidden on mobile horizontal to save space/avoid clutter */}
                  <div
                    className={`absolute bottom-0 left-0 w-full p-4 text-white ${mobileLayout === "horizontal" ? "hidden sm:block" : ""}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-sm font-bold">
                          {fitnessClass.instructorName}
                        </h3>
                        <p className="text-xs opacity-90">
                          {fitnessClass.instructorType &&
                            fitnessClass.instructorType.length > 0
                            ? fitnessClass.instructorType
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())
                            : "Fitness Instructor"}
                        </p>
                      </div>
                      <div className="bg-[#ff7043] px-3 py-2 rounded-lg flex items-center gap-1">
                        <CurrencyRupeeIcon className="w-4 h-4 text-white" />
                        {fitnessClass.pricing?.mode === "recurringClass" ? (
                          <div className="text-xs font-medium text-white">
                            <span>
                              ₹{fitnessClass.pricing.packagePrice}
                            </span>
                            <span className="text-[10px] opacity-75 ml-1">
                              ({fitnessClass.pricing.totalSessions} sessions)
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs font-medium text-white">
                            ₹
                            {price > 0
                              ? price
                              : (fitnessClass as any).price > 0
                                ? (fitnessClass as any).price
                                : (fitnessClass as any).totalAmount > 0
                                  ? (fitnessClass as any).totalAmount
                                  : "Contact"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Class Information */}
                <div
                  className={`${simplified ? "p-3" : "p-4"} flex flex-col flex-1 ${mobileLayout === "horizontal" ? "w-2/3 sm:w-full" : "w-full"}`}
                >
                  {/* Quick Stats */}
                  <div
                    className={`flex items-center gap-4 text-sm text-[#463a33]/70 ${simplified ? "mb-2" : "mb-3"}`}
                  >
                    {/* Only show rating if it exists and is meaningful */}
                    {fitnessClass.rating && fitnessClass.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <StarIcon className="w-4 h-4 text-[#ff7043]" />
                        <span>{fitnessClass.rating.toFixed(1)}</span>
                        {!simplified &&
                          fitnessClass.reviewCount &&
                          fitnessClass.reviewCount > 0 && (
                            <span className="text-xs">
                              ({fitnessClass.reviewCount})
                            </span>
                          )}
                      </div>
                    )}

                    {/* Show spots left only for non-booked classes */}
                    {!simplified && !(fitnessClass as any).bookingStatus && (
                      <div className="flex items-center gap-1">
                        <UserGroupIcon className="w-4 h-4 text-[#463a33]" />
                        <span
                          className={
                            currentSpots <= 5 ? "text-red-500 font-medium" : ""
                          }
                        >
                          {currentSpots} spots left
                        </span>
                      </div>
                    )}

                    {/* Duration */}
                    <div className="flex items-center gap-1">
                      <ClockIcon className="w-4 h-4 text-[#463a33]" />
                      <span>{scheduleInfo.duration}</span>
                    </div>

                    {/* Payment Status for booked classes */}
                    {(fitnessClass as any).paymentStatus &&
                      (fitnessClass as any).paymentStatus !== "unknown" &&
                      (fitnessClass as any).paymentStatus !== "" && (
                        <div className="flex items-center gap-1">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${(fitnessClass as any).paymentStatus ===
                              "completed"
                              ? "bg-green-100 text-green-700 border border-green-200"
                              : (fitnessClass as any).paymentStatus ===
                                "pending"
                                ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                : (fitnessClass as any).paymentStatus ===
                                  "failed"
                                  ? "bg-red-100 text-red-700 border border-red-200"
                                  : "bg-gray-100 text-gray-700 border border-gray-200"
                              }`}
                          >
                            {(fitnessClass as any).paymentStatus === "completed"
                              ? "✓ Paid"
                              : (fitnessClass as any).paymentStatus ===
                                "pending"
                                ? "⏳ Payment Pending"
                                : (fitnessClass as any).paymentStatus ===
                                  "failed"
                                  ? "✗ Payment Failed"
                                  : `Payment: ${(fitnessClass as any).paymentStatus}`}
                          </span>
                        </div>
                      )}
                  </div>

                  <h2
                    className={`text-base font-semibold text-[#463a33] ${simplified ? "mb-1" : "mb-2"} ${simplified ? "line-clamp-1" : "line-clamp-2"}`}
                  >
                    {fitnessClass.title}
                  </h2>
                  {/* Description */}
                  <p
                    className={`text-sm text-[#463a33]/80 ${simplified ? "mb-3" : "mb-4"} ${mobileLayout === "horizontal" ? "hidden sm:block" : ""}`}
                    style={
                      {
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      } as React.CSSProperties
                    }
                  >
                    {fitnessClass.description ||
                      "No description available for this class."}
                  </p>

                  {/* Date, Time, and Enhanced Schedule Info */}
                  <div
                    className={`flex flex-wrap gap-1.5 ${simplified ? "mb-3" : "mb-4"}`}
                  >
                    {/* Only show schedule if we have meaningful information */}
                    {scheduleInfo.date &&
                      scheduleInfo.time &&
                      scheduleInfo.date !== "Flexible" &&
                      scheduleInfo.time !== "Contact instructor" && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-orange-300 text-xs text-orange-700 bg-orange-50 font-medium shadow-sm">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          {scheduleInfo.date} • {scheduleInfo.time}
                        </span>
                      )}

                    {/* Show booking date for booked classes */}
                    {(fitnessClass as any).bookingDate && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-green-300 text-xs text-green-700 bg-green-50 font-medium shadow-sm">
                        <CalendarIcon className="w-3 h-3 mr-1" />
                        Booked:{" "}
                        {new Date(
                          (fitnessClass as any).bookingDate
                        ).toLocaleDateString()}
                      </span>
                    )}

                    {/* Show amount paid for booked classes */}
                    {(fitnessClass as any).totalAmount &&
                      (fitnessClass as any).totalAmount > 0 && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-green-400 text-xs text-green-800 bg-green-50 font-medium shadow-sm">
                          <CurrencyRupeeIcon className="w-3 h-3 mr-1" />
                          Paid: ₹{(fitnessClass as any).totalAmount}
                        </span>
                      )}

                    {simplified ? (
                      // Simplified view - show only essential info
                      <>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 bg-gray-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                        >
                          <VideoCameraIcon className="w-3 h-3 mr-1" />
                          Online
                        </span>
                        {!(fitnessClass as any).bookingStatus &&
                          currentSpots > 0 &&
                          currentSpots <= 3 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-red-300 text-xs text-red-700 bg-red-50 font-medium shadow-sm">
                              <FireIcon className="w-3 h-3 mr-1" />
                              Only {currentSpots} left!
                            </span>
                          )}
                      </>
                    ) : (
                      // Full detailed view
                      <>
                        {/* Format with enhanced info */}
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 bg-gray-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                        >
                          <VideoCameraIcon className="w-3 h-3 mr-1" />
                          Online
                        </span>

                        {/* Age Band Badge - only if not 'all' */}
                        {fitnessClass.ageBand &&
                          fitnessClass.ageBand !== "all" && (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full border border-blue-300 text-xs text-blue-700 bg-blue-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                            >
                              {fitnessClass.ageBand &&
                                fitnessClass.ageBand.charAt(0).toUpperCase() +
                                fitnessClass.ageBand.slice(1)}
                            </span>
                          )}

                        {/* Schedule Type with more details - only show if meaningful */}
                        {fitnessClass.schedule?.kind === "recurring" &&
                          fitnessClass.schedule.totalSessions && (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full border border-purple-300 text-xs text-purple-700 bg-purple-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                            >
                              <CalendarIcon className="w-3 h-3 mr-1" />
                              {fitnessClass.schedule.totalSessions} sessions
                            </span>
                          )}

                        {fitnessClass.schedule?.kind === "course" &&
                          fitnessClass.schedule.sessionDates?.length && (
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full border border-green-300 text-xs text-green-700 bg-green-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                            >
                              <AcademicCapIcon className="w-3 h-3 mr-1" />
                              {fitnessClass.schedule.sessionDates.length}{" "}
                              sessions
                            </span>
                          )}

                        {/* Low spots warning - only for non-booked classes */}
                        {!(fitnessClass as any).bookingStatus &&
                          currentSpots > 0 &&
                          currentSpots <= 3 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-red-300 text-xs text-red-700 bg-red-50 font-semibold shadow-sm">
                              <FireIcon className="w-3 h-3 mr-1" />
                              Only {currentSpots} left!
                            </span>
                          )}

                        {/* Difficulty level with more context - only show if not default */}
                        {Array.isArray(fitnessClass.difficulty) &&
                          fitnessClass.difficulty.length > 0 &&
                          fitnessClass.difficulty.some(
                            (d) => d && d !== "beginner"
                          ) ? (
                          fitnessClass.difficulty.map(
                            (diff, idx) =>
                              diff &&
                              diff !== "beginner" && (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 bg-gray-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                                >
                                  {String(diff).charAt(0).toUpperCase() +
                                    String(diff).slice(1)}
                                </span>
                              )
                          )
                        ) : fitnessClass.difficulty &&
                          Array.isArray(fitnessClass.difficulty) &&
                          fitnessClass.difficulty.length > 0 &&
                          !fitnessClass.difficulty.includes(
                            Difficulty.Beginner
                          ) ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 bg-gray-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                          >
                            {fitnessClass.difficulty &&
                              Array.isArray(fitnessClass.difficulty)
                              ? fitnessClass.difficulty[0]
                                .charAt(0)
                                .toUpperCase() +
                              fitnessClass.difficulty[0].slice(1)
                              : "N/A"}
                          </span>
                        ) : null}

                        {/* Only show language if not English or multiple languages */}
                        {Array.isArray(fitnessClass.languages) &&
                          fitnessClass.languages.length > 1 ? (
                          fitnessClass.languages.map((language, idx) => (
                            <span
                              key={idx}
                              className={`inline-flex items-center px-2.5 py-1 rounded-full border border-gray-300 text-xs text-gray-700 bg-gray-50 font-medium shadow-sm ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                            >
                              <LanguageIcon className="w-3 h-3 mr-1" />
                              {language}
                            </span>
                          ))
                        ) : fitnessClass.languages &&
                          Array.isArray(fitnessClass.languages) &&
                          fitnessClass.languages.length === 1 &&
                          fitnessClass.languages[0] !== "English" ? (
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full border border-[#463a33] text-xs text-[#463a33] bg-[#463a33]/5 ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                          >
                            <LanguageIcon className="w-3 h-3 mr-1" />
                            {Array.isArray(fitnessClass.languages)
                              ? fitnessClass.languages[0]
                              : fitnessClass.languages}
                          </span>
                        ) : null}
                        {(!fitnessClass.languages ||
                          (Array.isArray(fitnessClass.languages) &&
                            fitnessClass.languages.length === 0)) && (
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full border border-[#463a33] text-xs text-[#463a33] bg-[#463a33]/5 ${mobileLayout === "horizontal" ? "hidden sm:inline-flex" : ""}`}
                            >
                              <LanguageIcon className="w-3 h-3 mr-1" />
                              English
                            </span>
                          )}
                      </>
                    )}
                  </div>

                  {/* Features Display - Hidden */}
                  {/* Features section removed for cleaner card display */}

                  {/* Language Pills */}

                  {/* Call-to-Action Buttons */}
                  {/* Call-to-Action Buttons */}
                  <div className="flex gap-2 mt-auto pt-4">
                    {/* Show different buttons based on booking status */}
                    {(fitnessClass as any).isBooked === true ? (
                      // For booked classes, show Booked button
                      <>
                        <button
                          disabled
                          className="flex-1 flex items-center justify-center gap-2 bg-green-100 text-green-700 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          Booked
                        </button>
                        <Link
                          href={`/classes/${fitnessClass.id}`}
                          className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                        >
                          <VideoCameraIcon className="w-4 h-4" />
                          Details
                        </Link>
                      </>
                    ) : (fitnessClass as any).bookingStatus ? (
                      // For booked classes with detailed booking status
                      <>
                        {(fitnessClass as any).bookingStatus === "confirmed" &&
                          (fitnessClass as any).paymentStatus === "paid" ? (
                          <>
                            <button
                              disabled
                              className="flex-1 flex items-center justify-center gap-2 bg-green-100 text-green-700 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                              Booked
                            </button>
                            <Link
                              href={`/classes/${fitnessClass.id}`}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                            >
                              <VideoCameraIcon className="w-4 h-4" />
                              Details
                            </Link>
                          </>
                        ) : (fitnessClass as any).bookingStatus ===
                          "confirmed" &&
                          (fitnessClass as any).paymentStatus === "pending" ? (
                          <>
                            <button
                              disabled
                              className="flex-1 flex items-center justify-center gap-2 bg-orange-100 text-orange-700 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >
                              <ExclamationCircleIcon className="w-4 h-4" />
                              Payment Pending
                            </button>
                            <Link
                              href={`/classes/${fitnessClass.id}`}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                            >
                              <VideoCameraIcon className="w-4 h-4" />
                              Complete Payment
                            </Link>
                          </>
                        ) : (fitnessClass as any).bookingStatus ===
                          "pending" ? (
                          <>
                            <button
                              disabled
                              className="flex-1 flex items-center justify-center gap-2 bg-yellow-100 text-yellow-700 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >
                              <ExclamationCircleIcon className="w-4 h-4" />
                              Booking Pending
                            </button>
                            <Link
                              href={`/classes/${fitnessClass.id}`}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                            >
                              <VideoCameraIcon className="w-4 h-4" />
                              Details
                            </Link>
                          </>
                        ) : (
                          // Cancelled status
                          <>
                            <button
                              disabled
                              className="flex-1 flex items-center justify-center gap-2 bg-red-100 text-red-700 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >
                              <XMarkIcon className="w-4 h-4" />
                              Cancelled
                            </button>
                            <button
                              onClick={() => handleBookingClick(fitnessClass)}
                              disabled={currentSpots <= 0}
                              className="flex-1 flex items-center justify-center gap-2 bg-[#ff7043] text-white text-xs py-2.5 px-4 rounded-lg hover:bg-[#ff7043]/90 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                              <BookOpenIcon className="w-4 h-4" />
                              Book Again
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      // For regular classes (not booked), show normal booking buttons
                      <>
                        {/* Check if class is expired */}
                        {isClassExpired(fitnessClass) ? (
                          <>
                            <button
                              disabled
                              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-500 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >
                              <XMarkIcon className="w-4 h-4" />
                              Class Ended
                            </button>
                            <Link
                              href={`/classes/${fitnessClass.id}`}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                            >
                              <VideoCameraIcon className="w-4 h-4" />
                              Details
                            </Link>
                          </>
                        ) : (fitnessClass as any).isBooked ? (
                          // Check if this class is already booked by the user
                          <>
                            <button
                              disabled
                              className="flex-1 flex items-center justify-center gap-2 bg-green-100 text-green-700 text-xs py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                              Booked
                            </button>
                            <Link
                              href={`/classes/${fitnessClass.id}`}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                            >
                              <VideoCameraIcon className="w-4 h-4" />
                              Details
                            </Link>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleBookingClick(fitnessClass)}
                              disabled={currentSpots <= 0}
                              className="flex-1 flex items-center justify-center gap-2 bg-[#ff7043] text-white text-xs py-2.5 px-4 rounded-lg hover:bg-[#ff7043]/90 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                              <BookOpenIcon className="w-4 h-4" />
                              {currentSpots <= 0 ? "Fully Booked" : "Enroll"}
                            </button>
                            <Link
                              href={`/classes/${fitnessClass.id}`}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                            >
                              <VideoCameraIcon className="w-4 h-4" />
                              Details
                            </Link>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Transition appear show={!!bookingClass} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setBookingClass(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-[#463a33]/60 backdrop-blur-sm" />
          </Transition.Child>

          {/* Mobile: Bottom Drawer, Desktop: Centered Modal */}
          <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500"
              enterFrom="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
              enterTo="translate-y-0 sm:opacity-100 sm:scale-100"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-y-0 sm:opacity-100 sm:scale-100"
              leaveTo="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-y-auto">
                {/* Drag Handle - Only visible on mobile */}
                <div className="sm:hidden flex justify-center pt-3 pb-2 bg-white rounded-t-3xl">
                  <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
                </div>

                {bookingClass && (
                  <PaymentFirstBooking
                    classData={{
                      id: bookingClass.id,
                      title: bookingClass.title,
                      instructorName: bookingClass.instructorName,
                      price: getBaseAmount(bookingClass),
                      capacity: bookingClass.capacity,
                      currentEnrollment:
                        bookingClass.capacity - (bookingClass.spotsLeft || 0),
                      duration: bookingClass.schedule.durationMin || 60,
                      date: (() => {
                        try {
                          // 1. Try to get correct date directly from schedule to avoid parsing display strings
                          if (bookingClass.schedule?.kind === 'single' && bookingClass.schedule.date) {
                            return new Date(bookingClass.schedule.date).toISOString();
                          }
                          if (bookingClass.schedule?.kind === 'recurring' && bookingClass.schedule.startDate) {
                            // Combine startDate with startTime if available
                            const d = new Date(bookingClass.schedule.startDate);
                            if (bookingClass.schedule.startTime) {
                              const [hours, minutes] = bookingClass.schedule.startTime.split(':');
                              d.setHours(parseInt(hours) || 0, parseInt(minutes) || 0);
                            }
                            return d.toISOString();
                          }

                          // 2. Fallback to existing logic but with safety checks
                          const dateTime = getClassDateTime(bookingClass);
                          if (!dateTime.date || dateTime.date === 'TBD') return undefined;

                          const d = new Date(`${dateTime.date} ${dateTime.time}`);
                          return isNaN(d.getTime()) ? undefined : d.toISOString();
                        } catch (e) {
                          console.error("Error parsing date for booking:", e);
                          return undefined;
                        }
                      })(),
                    }}
                    userId={user?.id || ""}
                    onBookingSuccess={(booking) => {
                      console.log("Booking successful:", booking);
                      // Update spot count
                      handleSpotUpdate(
                        bookingClass.id,
                        Math.max(0, (bookingClass.spotsLeft || 0) - 1)
                      );
                      // Call parent callback
                      if (onBookingSuccess) {
                        onBookingSuccess();
                      }
                      setBookingClass(null);
                      toast.success("Class booked successfully!");
                    }}
                    onBookingError={(error) => {
                      console.error("Booking error:", error);
                      toast.error(error);
                      setBookingClass(null);
                    }}
                    onClose={() => setBookingClass(null)}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Image Overlay */}
      <ImageOverlay
        isOpen={imageOverlay.isOpen}
        onClose={() =>
          setImageOverlay({
            isOpen: false,
            imageSrc: "",
            imageAlt: "",
            title: "",
          })
        }
        imageSrc={imageOverlay.imageSrc}
        imageAlt={imageOverlay.imageAlt}
        title={imageOverlay.title}
      />
    </>
  );
};

export default ClassList;
